import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { fmtDateTime } from "@/lib/format";

interface RuleRow {
  id: string;
  name: string;
  enabled: boolean;
  event_type: string;
  conditions: Array<{ field: string; operator: string; value: unknown }>;
  actions: Array<{ type: string }>;
  last_triggered_at: string | null;
  trigger_count: number;
  error_count: number;
  max_retries: number;
}

interface LogRow {
  id: string;
  rule_id: string;
  event_type: string;
  condition_result: boolean;
  execution_result: string;
  action_type: string | null;
  target_entity_id: string | null;
  duration_ms: number | null;
  created_at: string;
  error_message: string | null;
  retryable: boolean;
  attempt_count: number;
  next_retry_at: string | null;
  action_results: Array<{ action: string; status: string; error?: string }>;
}

const EVENT_TYPES = [
  { value: "InventoryChanged", label: "Inventory — Changed" },
  { value: "SalesOrderCreated", label: "Sales Order — Created" },
  { value: "SalesOrderAllocated", label: "Sales Order — Allocated" },
  { value: "PurchaseOrderReceived", label: "Purchase — Received" },
  { value: "listing.sync_failed", label: "Listing — Sync failed" },
  { value: "provider.dead_lettered", label: "Provider — Dead lettered" },
];

const CONDITION_FIELDS: Record<string, Array<{ value: string; label: string }>> = {
  "InventoryChanged": [
    { value: "available", label: "Available qty" },
    { value: "on_hand", label: "On hand qty" },
    { value: "days_of_stock", label: "Days of stock" },
  ],
  "SalesOrderCreated": [
    { value: "priority", label: "Priority" },
    { value: "source", label: "Source" },
    { value: "total", label: "Order total" },
  ],
  "SalesOrderAllocated": [
    { value: "priority", label: "Priority" },
    { value: "status", label: "Status" },
  ],
  "PurchaseOrderReceived": [
    { value: "supplier_id", label: "Supplier ID" },
    { value: "status", label: "Status" },
  ],
  "listing.sync_failed": [
    { value: "provider", label: "Provider" },
    { value: "error_class", label: "Error class" },
  ],
  "provider.dead_lettered": [
    { value: "provider", label: "Provider" },
  ],
};

const OPERATORS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "≠" },
  { value: "greater_than", label: ">" },
  { value: "less_than", label: "<" },
  { value: "greater_or_equal", label: "≥" },
  { value: "less_or_equal", label: "≤" },
  { value: "in", label: "in (comma-sep)" },
  { value: "not_in", label: "not in (comma-sep)" },
];

const ALLOWED_ACTIONS = [
  { value: "refresh_notification", label: "Refresh operational notification" },
  { value: "create_replenishment", label: "Refresh replenishment plan & notify" },
  { value: "attempt_allocation", label: "Attempt order allocation" },
  { value: "create_pick_tasks", label: "Create pick tasks" },
  { value: "create_exception", label: "Create exception" },
  { value: "queue_marketplace_sync", label: "Queue marketplace sync" },
  { value: "queue_fulfilment_sync", label: "Queue fulfilment sync" },
];

type WizardStep = "trigger" | "conditions" | "action" | "review";

const STEP_LABELS: Record<WizardStep, string> = {
  trigger: "1. Trigger",
  conditions: "2. Conditions",
  action: "3. Action",
  review: "4. Review",
};

const STEPS: WizardStep[] = ["trigger", "conditions", "action", "review"];

export default function AutomationRules() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [logsRuleId, setLogsRuleId] = useState<string | null>(null);
  const [retryingLogId, setRetryingLogId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [wizardStep, setWizardStep] = useState<WizardStep>("trigger");

  const [form, setForm] = useState({
    name: "",
    event_type: "InventoryChanged",
    conditions: [{ field: "available", operator: "less_than", value: "10" }],
    action_type: "refresh_notification",
    rate_limit_per_minute: 10,
    description: "",
  });

  const isAdmin = role === "owner" || role === "admin";

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["automation-rules", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("automation_rules")
        .select("id, name, enabled, event_type, conditions, actions, last_triggered_at, trigger_count, error_count, max_retries")
        .eq("org_id", org!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (rows ?? []) as RuleRow[];
    },
  });

  const { data: logsData } = useQuery({
    queryKey: ["automation-logs", org?.id, logsRuleId],
    enabled: !!org?.id && !!logsRuleId,
    queryFn: async () => {
      const { data: logs } = await supabase
        .from("automation_rule_logs")
        .select("id, rule_id, event_type, condition_result, execution_result, action_type, target_entity_id, duration_ms, created_at, error_message, retryable, attempt_count, next_retry_at, action_results")
        .eq("org_id", org!.id)
        .eq("rule_id", logsRuleId!)
        .order("created_at", { ascending: false })
        .limit(50);
      return (logs ?? []) as LogRow[];
    },
  });

  const resetForm = () => {
    setForm({ name: "", event_type: "InventoryChanged", conditions: [{ field: "available", operator: "less_than", value: "10" }], action_type: "refresh_notification", rate_limit_per_minute: 10, description: "" });
    setWizardStep("trigger");
    setError("");
  };

  const handleCreate = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim()) { setError("Rule name is required."); return; }
    setError("");
    setSaving(true);
    const conditions = form.conditions.filter((c) => c.field && c.value !== "");
    const actions = [{ type: form.action_type }];
    const { error: insErr } = await supabase.from("automation_rules").insert({
      org_id: org!.id, name: form.name, event_type: form.event_type, conditions, actions, enabled: true, max_retries: 3,
      rate_limit_per_minute: form.rate_limit_per_minute, description: form.description || null,
    });
    setSaving(false);
    if (insErr) { setError(insErr.message); return; }
    setCreateOpen(false);
    resetForm();
    queryClient.invalidateQueries({ queryKey: ["automation-rules", org?.id] });
  };

  const toggleEnabled = async (ruleId: string, current: boolean) => {
    await supabase.from("automation_rules").update({ enabled: !current }).eq("id", ruleId);
    queryClient.invalidateQueries({ queryKey: ["automation-rules", org?.id] });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setSaving(true);
    await supabase.from("automation_rules").delete().eq("id", deleteId);
    setSaving(false);
    setDeleteId(null);
    queryClient.invalidateQueries({ queryKey: ["automation-rules", org?.id] });
  };

  const handleTest = async (ruleId: string) => {
    setTestingId(ruleId);
    try {
      await supabase.rpc("run_automation_rule_test", { p_rule_id: ruleId });
      queryClient.invalidateQueries({ queryKey: ["automation-rules", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["automation-logs", org?.id, ruleId] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTestingId(null);
    }
  };

  const handleRetryLog = async (logId: string) => {
    setRetryingLogId(logId);
    setError("");
    try {
      const { data, error: rErr } = await supabase.rpc("retry_automation_log", { p_log_id: logId });
      if (rErr) throw rErr;
      if (data && data.ok === false) setError((data.error as string) ?? "Retry failed.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRetryingLogId(null);
      queryClient.invalidateQueries({ queryKey: ["automation-logs", org?.id, logsRuleId] });
      queryClient.invalidateQueries({ queryKey: ["automation-rules", org?.id] });
    }
  };

  const addCondition = () =>
    setForm((f) => ({
      ...f,
      conditions: [...f.conditions, { field: (CONDITION_FIELDS[f.event_type] ?? [])[0]?.value ?? "available", operator: "equals", value: "" }],
    }));

  const updateCondition = (idx: number, patch: Partial<{ field: string; operator: string; value: string }>) =>
    setForm((f) => ({ ...f, conditions: f.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));

  const removeCondition = (idx: number) =>
    setForm((f) => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }));

  // Human-readable rule summary for Review step
  const ruleSummary = () => {
    const eventLabel = EVENT_TYPES.find((e) => e.value === form.event_type)?.label ?? form.event_type;
    const actionLabel = ALLOWED_ACTIONS.find((a) => a.value === form.action_type)?.label ?? form.action_type;
    const condParts = form.conditions.filter((c) => c.value !== "").map((c) => {
      const opLabel = OPERATORS.find((o) => o.value === c.operator)?.label ?? c.operator;
      return `${c.field} ${opLabel} ${c.value}`;
    });
    return { eventLabel, actionLabel, condParts };
  };

  const canProceed = () => {
    if (wizardStep === "trigger") return !!form.event_type;
    if (wizardStep === "conditions") return true;
    if (wizardStep === "action") return !!form.action_type;
    return !!form.name.trim();
  };

  return (
    <div>
      <PageHeader title="Automation Rules" subtitle="Automatically respond to inventory, order and marketplace events">
        {isAdmin && (
          <button onClick={() => { resetForm(); setCreateOpen(true); }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" /> New Rule
          </button>
        )}
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8"><TableSkeleton /></div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500 mb-4">Couldn't load rules.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
          </div>
        ) : (data ?? []).length === 0 ? (
          <EmptyState icon="ri-robot-2-line" title="No automation rules" description="Create rules to automatically respond to inventory, orders, and provider events. Action types are backend-enforced." action={
            isAdmin ? <button onClick={() => { resetForm(); setCreateOpen(true); }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"><i className="ri-add-line" /> Create first rule</button> : undefined
          } />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rule</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">When</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Then</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Runs</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Errors</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((rule) => {
                  const eventLabel = EVENT_TYPES.find((e) => e.value === rule.event_type)?.label ?? rule.event_type;
                  const actionLabel = ALLOWED_ACTIONS.find((a) => a.value === (rule.actions?.[0]?.type ?? ""))?.label ?? (rule.actions?.[0]?.type ?? "—");
                  return (
                    <tr key={rule.id} className="border-b border-slate-50 hover:bg-slate-50/40">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">{rule.name}</p>
                        <p className="text-xs text-slate-400">Last: {rule.last_triggered_at ? fmtDateTime(rule.last_triggered_at) : "Never"}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-slate-700">{eventLabel}</p>
                        {(rule.conditions ?? []).filter((c) => c.value !== "" && c.value != null).map((c, i) => (
                          <span key={i} className="text-[10px] text-slate-400 block">{c.field} {c.operator} {String(c.value)}</span>
                        ))}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{actionLabel}</span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => isAdmin && toggleEnabled(rule.id, rule.enabled)}
                          disabled={!isAdmin}
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${rule.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                        >
                          {rule.enabled ? "Enabled" : "Disabled"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{rule.trigger_count ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        {(rule.error_count ?? 0) > 0 ? <span className="text-red-600 font-medium">{rule.error_count}</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setLogsRuleId(rule.id)}
                            className="px-2 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap"
                            title="View execution logs"
                          >
                            <i className="ri-history-line" />
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => handleTest(rule.id)}
                                disabled={testingId === rule.id || !rule.enabled}
                                className="px-2 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap disabled:opacity-50"
                                title="Send a test event to this rule"
                              >
                                {testingId === rule.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-play-line" />}
                              </button>
                              <button
                                onClick={() => setDeleteId(rule.id)}
                                className="px-2 py-1.5 bg-white border border-slate-200 hover:bg-red-50 hover:text-red-700 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap"
                              >
                                <i className="ri-delete-bin-line" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Execution Logs Modal */}
      <Modal
        open={!!logsRuleId}
        onClose={() => setLogsRuleId(null)}
        title={`Execution Log — ${(data ?? []).find((r) => r.id === logsRuleId)?.name ?? "Rule"}`}
        wide
        footer={<button onClick={() => setLogsRuleId(null)} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Close</button>}
      >
        {(logsData ?? []).length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">No execution logs yet. Enable the rule and trigger a test event.</div>
        ) : (
          <div className="divide-y divide-slate-50 max-h-[60vh] overflow-y-auto">
            {(logsData ?? []).map((log) => (
              <div key={log.id} className="py-3 px-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${log.execution_result === "success" ? "bg-emerald-100 text-emerald-700" : log.execution_result === "condition_not_met" ? "bg-slate-100 text-slate-600" : "bg-red-100 text-red-700"}`}>
                    {log.execution_result}
                  </span>
                  <span className="text-xs text-slate-500">{log.event_type}</span>
                  {log.action_type && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{log.action_type}</span>}
                  {log.duration_ms != null && <span className="text-xs text-slate-400">{log.duration_ms}ms</span>}
                  <span className="text-xs text-slate-400 ml-auto">{fmtDateTime(log.created_at)}</span>
                </div>
                {(log.action_results ?? []).length > 0 && (
                  <div className="text-xs text-slate-500 space-y-0.5 mt-1">
                    {log.action_results.map((ar, i) => (
                      <span key={i} className={`inline-block mr-2 ${ar.status === "success" ? "text-emerald-600" : "text-red-500"}`}>
                        {ar.action}: {ar.status}{ar.error ? ` — ${ar.error}` : ""}
                      </span>
                    ))}
                  </div>
                )}
                {log.execution_result === "failed" && (
                  <div className="flex items-center gap-3 mt-2">
                    {log.error_message && <span className="text-xs text-red-500 flex-1">{log.error_message}</span>}
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">Attempt {log.attempt_count ?? 1}</span>
                    {isAdmin && log.retryable !== false && (
                      <button
                        onClick={() => handleRetryLog(log.id)}
                        disabled={retryingLogId === log.id}
                        className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-medium rounded-lg whitespace-nowrap inline-flex items-center gap-1"
                      >
                        <i className="ri-refresh-line" />
                        {retryingLogId === log.id ? "Retrying..." : "Retry"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Rule Builder Wizard */}
      <Modal
        open={createOpen}
        onClose={() => { setCreateOpen(false); resetForm(); }}
        title="New Automation Rule"
        wide
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-1">
              {STEPS.map((s) => (
                <button
                  key={s}
                  onClick={() => setWizardStep(s)}
                  className={`px-3 py-1 text-xs rounded-full font-medium whitespace-nowrap ${s === wizardStep ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:text-slate-700"}`}
                >
                  {STEP_LABELS[s]}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setCreateOpen(false); resetForm(); }} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
              {wizardStep !== "review" ? (
                <button
                  onClick={() => setWizardStep(STEPS[STEPS.indexOf(wizardStep) + 1])}
                  disabled={!canProceed()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={saving || !form.name.trim()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  {saving ? "Creating..." : "Create Rule"}
                </button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

          {wizardStep === "trigger" && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-800">Step 1: Choose a trigger event</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EVENT_TYPES.map((et) => (
                  <button
                    key={et.value}
                    onClick={() => setForm((f) => ({ ...f, event_type: et.value, conditions: [{ field: (CONDITION_FIELDS[et.value] ?? [])[0]?.value ?? "available", operator: "less_than", value: "" }] }))}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${form.event_type === et.value ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <i className={`text-xl ${form.event_type === et.value ? "ri-checkbox-circle-fill text-emerald-600" : "ri-circle-line text-slate-400"}`} />
                    <span className="text-sm font-medium text-slate-800">{et.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {wizardStep === "conditions" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800">Step 2: Add conditions (optional)</h3>
                <button onClick={addCondition} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap font-medium"><i className="ri-add-line" /> Add condition</button>
              </div>
              {form.conditions.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl">No conditions — rule will trigger on every matching event.</p>
              ) : (
                <div className="space-y-2">
                  {form.conditions.map((c, i) => {
                    const fieldOptions = CONDITION_FIELDS[form.event_type] ?? [];
                    return (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                        <select value={c.field} onChange={(e) => updateCondition(i, { field: e.target.value })} className="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs">
                          {fieldOptions.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                        <select value={c.operator} onChange={(e) => updateCondition(i, { operator: e.target.value })} className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs">
                          {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <input value={c.value} onChange={(e) => updateCondition(i, { value: e.target.value })} placeholder="Value" className="w-24 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs" />
                        <button onClick={() => removeCondition(i)} className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-500 rounded">
                          <i className="ri-close-line text-sm" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-slate-400">All conditions must be satisfied for the rule to trigger. Leave empty to trigger on every matching event.</p>
            </div>
          )}

          {wizardStep === "action" && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-800">Step 3: Choose an action</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ALLOWED_ACTIONS.map((a) => (
                  <button
                    key={a.value}
                    onClick={() => setForm((f) => ({ ...f, action_type: a.value }))}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${form.action_type === a.value ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <i className={`text-xl ${form.action_type === a.value ? "ri-checkbox-circle-fill text-emerald-600" : "ri-circle-line text-slate-400"}`} />
                    <span className="text-sm font-medium text-slate-800">{a.label}</span>
                  </button>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
                <i className="ri-lock-line mr-1" />
                Only these backend-approved actions can execute. No arbitrary code, SQL, HTTP, or external calls.
              </div>
            </div>
          )}

          {wizardStep === "review" && (() => {
            const { eventLabel, actionLabel, condParts } = ruleSummary();
            return (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-800">Step 4: Review and name your rule</h3>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Rule name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                    placeholder="e.g. Low stock — auto replenishment alert"
                    autoFocus
                  />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 text-sm">
                  <p className="font-semibold text-slate-700">Rule summary</p>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-slate-400 w-14 shrink-0 mt-0.5">When:</span>
                    <span className="text-slate-800">{eventLabel}</span>
                  </div>
                  {condParts.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold text-slate-400 w-14 shrink-0 mt-0.5">If:</span>
                      <span className="text-slate-800">{condParts.join(" AND ")}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-slate-400 w-14 shrink-0 mt-0.5">Then:</span>
                    <span className="text-emerald-700 font-medium">{actionLabel}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-slate-400 w-14 shrink-0 mt-0.5">Safety:</span>
                    <span className="text-slate-600 text-xs">Runs once per event · Retries up to 3 times automatically</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete automation rule"
        message="Delete this rule? It will stop triggering immediately. Historical logs are preserved."
        confirmLabel="Delete"
        loading={saving}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}